import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { BrowserRouter, Link, Navigate, NavLink, Route, Routes, useLocation, useParams } from 'react-router-dom'
import {
  advantages,
  bankClients,
  companyStats,
  heroImage,
  manufacturingImages,
  manufacturingStats,
  navItems,
  processSteps,
  projects,
  qualifications,
  solutions,
} from './data/siteData'
import type { ImageAsset, Project } from './data/siteData'

const mainNavItems = [
  { label: '首页', href: '/' },
  { label: '解决方案', href: '/solutions' },
  { label: '项目案例', href: '/projects' },
  { label: '制造实力', href: '/manufacturing' },
  { label: '项目服务', href: '/service' },
  { label: '关于昊野', href: '/about' },
  { label: '资质荣誉', href: '/qualifications' },
  { label: '联系我们', href: '/contact' },
]

const solutionRoutes = [
  { slug: 'exterior', path: '/solutions/exterior' },
  { slug: 'interior', path: '/solutions/interior' },
  { slug: 'soft-decoration', path: '/solutions/soft-decoration' },
  { slug: 'atm', path: '/solutions/atm' },
  { slug: 'upgrade', path: '/solutions/upgrade' },
]

const contactPhone = '18620471014'
const contactEmail = '330678233@qq.com'
const companyAddress = '广东省佛山市顺德区北滘镇顺江社区三乐东路25号集成科创园5栋103室、204室'
const companyMapUrl = 'https://surl.amap.com/8A3UaHkQfJz'
const wechatQrCode = '/assets/wechat-qr.jpg'

const serviceGuarantees = [
  { id: '01', icon: '/images/service-guarantees/response.png', title: '全国项目响应', description: '支持跨区域、多网点项目统筹及快速响应。' },
  { id: '02', icon: '/images/service-guarantees/production.png', title: '标准化生产管理', description: '依托自有生产体系，保障批量项目标准统一与稳定交付。' },
  { id: '03', icon: '/images/service-guarantees/acceptance.png', title: '工程安装与验收', description: '覆盖现场施工、安装协调、质量检查及项目验收。' },
  { id: '04', icon: '/images/service-guarantees/after-sales.png', title: '长期售后维护', description: '提供持续维护、升级及长期项目服务支持。' },
]

const projectServiceCapabilities = ['设计深化', '标准化生产', '工程实施', '安装验收', '售后服务']

type SolutionDetailConfig = {
  slug: string
  heroPoints: string[]
  heroPointIcons?: string[]
  overview: string
  serviceIcons?: string[]
  services: Array<[string, string]>
  scenes: string[]
  process: Array<[string, string]>
  processIcons?: string[]
  strengths: string[]
  strengthIcons?: string[]
  relatedScopes: string[]
}

const solutionDetailPages: SolutionDetailConfig[] = [
  {
    slug: 'exterior',
    heroPoints: ['品牌标准化', '结构安全可靠', '亮度与能耗控制', '全国服务响应'],
    heroPointIcons: [
      '/images/solutions/exterior/icons/hero/hero-brand-standard.png',
      '/images/solutions/exterior/icons/hero/hero-structure-safe.png',
      '/images/solutions/exterior/icons/hero/hero-energy-control.png',
      '/images/solutions/exterior/icons/hero/hero-national-service.png',
    ],
    overview: '针对银行网点新建、迁址、改造及批量VI换版需求，提供门楣招牌、外立面标识、24小时自助区标识等深化设计、生产制造与安装交付支持。',
    serviceIcons: [
      '/images/solutions/exterior/icons/services/service-fascia-sign.png',
      '/images/solutions/exterior/icons/services/service-light-logo.png',
      '/images/solutions/exterior/icons/services/service-vertical-sign.png',
      '/images/solutions/exterior/icons/services/service-24h.png',
      '/images/solutions/exterior/icons/services/service-wayfinding.png',
      '/images/solutions/exterior/icons/services/service-upgrade.png',
    ],
    services: [
      ['门楣招牌', '标准化设计与制作，提升品牌识别效果。'],
      ['发光LOGO / 字体', '亮度、材质、工艺与视觉规范统一。'],
      ['竖式招牌 / 灯箱', '多种工程场景适配，满足不同建筑界面。'],
      ['24小时自助银行标识', '形成夜间与自助服务场景识别。'],
      ['外立面导向标识', '指引清晰，完善客户到达路径。'],
      ['旧门头升级改造', '拆换更新一体化，安全高效交付。'],
    ],
    scenes: ['新建网点', '网点迁址', '旧网点升级', '批量VI改造', '临街 / 商场 / 写字楼网点'],
    process: [
      ['现场条件确认', '测量界面尺寸，了解现场施工条件。'],
      ['VI深化设计', '根据规范完成图纸深化与效果确认。'],
      ['工厂定制制作', '开展工艺制造、品控及预装检查。'],
      ['物流安装实施', '安全包装、进场安装与固定调试。'],
      ['现场安装验收', '专业安装收口，配合验收交付。'],
    ],
    processIcons: [
      '/images/solutions/exterior/icons/process/process-site-check.png',
      '/images/solutions/exterior/icons/process/process-vi-design.png',
      '/images/solutions/exterior/icons/process/process-production.png',
      '/images/solutions/exterior/icons/process/process-logistics.png',
      '/images/solutions/exterior/icons/process/process-acceptance.png',
    ],
    strengths: ['VI规范精准落地', '结构深化专业', '灯光效果出众', '户外材质耐用', '批量交付能力强', '全国服务网络'],
    strengthIcons: [
      '/images/solutions/exterior/icons/advantages/advantage-vi-standard.png',
      '/images/solutions/exterior/icons/advantages/advantage-structure.png',
      '/images/solutions/exterior/icons/advantages/advantage-lighting.png',
      '/images/solutions/exterior/icons/advantages/advantage-material.png',
      '/images/solutions/exterior/icons/advantages/advantage-batch-delivery.png',
      '/images/solutions/exterior/icons/advantages/advantage-national-network.png',
    ],
    relatedScopes: ['外立面', '门楣', '24小时区', '夜间识别'],
  },
  {
    slug: 'interior',
    heroPoints: ['导视层级清晰', '空间协调统一', '模块维护便捷', '安装深化可靠'],
    heroPointIcons: [
      '/images/solutions/interior/icons/hero/hero-wayfinding-level.png',
      '/images/solutions/interior/icons/hero/hero-space-coordination.png',
      '/images/solutions/interior/icons/hero/hero-modular-maintenance.png',
      '/images/solutions/interior/icons/hero/hero-installation-reliable.png',
    ],
    overview: '围绕银行营业厅内部服务动线与VI规范，提供功能区标识、吊牌导向、柜台窗口标识及业务指引相关深化、制作与安装服务。',
    serviceIcons: [
      '/images/solutions/interior/icons/services/service-hours-sign.png',
      '/images/solutions/interior/icons/services/service-zone-sign.png',
      '/images/solutions/interior/icons/services/service-hanging-wayfinding.png',
      '/images/solutions/interior/icons/services/service-counter-window.png',
      '/images/solutions/interior/icons/services/service-guidance-sign.png',
      '/images/solutions/interior/icons/services/service-consumer-protection.png',
    ],
    services: [
      ['营业时间牌', '规范营业信息展示，保持入口识别统一。'],
      ['功能区标识', '梳理业务分区，提高客户识别效率。'],
      ['吊牌 / 导向牌', '结合动线和视距完成导向深化。'],
      ['柜台 / 窗口标识', '明确业务窗口与服务触点。'],
      ['业务指引牌', '强化办理路径和业务说明可读性。'],
      ['消费者权益保护宣传标识', '匹配金融服务场景宣传要求。'],
    ],
    scenes: ['综合营业厅', '财富管理区', 'VIP区', '智能银行区', '等候区', '自助服务区'],
    process: [
      ['空间动线梳理', '确认客户路径与服务触点位置。'],
      ['标识层级设计', '匹配阅读距离、安装高度与信息层级。'],
      ['模块化制作', '按规范进行标准化生产与维护设计。'],
      ['现场安装协调', '配合装修界面完成安装收口。'],
      ['交付检查', '检查位置、牢固度、清晰度与一致性。'],
    ],
    processIcons: [
      '/images/solutions/interior/icons/process/process-circulation.png',
      '/images/solutions/interior/icons/process/process-hierarchy-design.png',
      '/images/solutions/interior/icons/process/process-modular-production.png',
      '/images/solutions/interior/icons/process/process-installation-coordination.png',
      '/images/solutions/interior/icons/process/process-quality-check.png',
    ],
    strengths: ['导视层级', '阅读距离', '空间协调', 'VI一致性', '模块化维护', '安装深化'],
    strengthIcons: [
      '/images/solutions/interior/icons/advantages/advantage-wayfinding-level.png',
      '/images/solutions/interior/icons/advantages/advantage-reading-distance.png',
      '/images/solutions/interior/icons/advantages/advantage-space-coordination.png',
      '/images/solutions/interior/icons/advantages/advantage-vi-consistency.png',
      '/images/solutions/interior/icons/advantages/advantage-modular-maintenance.png',
      '/images/solutions/interior/icons/advantages/advantage-installation-detail.png',
    ],
    relatedScopes: ['室内VI', '导视', '服务区', '营销展示'],
  },
  {
    slug: 'soft-decoration',
    heroPoints: ['设计意图落地', '材料工艺深化', '多材料组合', '现场细节收口'],
    heroPointIcons: [
      '/images/solutions/soft-decoration/icons/hero/hero-design-realization.png',
      '/images/solutions/soft-decoration/icons/hero/hero-material-detail.png',
      '/images/solutions/soft-decoration/icons/hero/hero-material-combination.png',
      '/images/solutions/soft-decoration/icons/hero/hero-detail-finishing.png',
    ],
    overview: '面向主题网点、品牌展示区及金融教育空间，协助完成文化墙、展示装置、墙面装饰系统等软装视觉元素的深化制作与落地安装。',
    services: [
      ['主题文化墙', '围绕网点主题完成展示内容承载。'],
      ['品牌展示装置', '将品牌信息转化为可落地装置。'],
      ['墙面装饰系统', '匹配材料、尺寸与现场安装界面。'],
      ['定制展示', '配合特色空间完成展示构件制作。'],
      ['灯光展示元素', '强化重点区域的视觉识别。'],
      ['宣传陈列装置', '支持金融服务及品牌宣传展示。'],
    ],
    serviceIcons: [
      '/images/solutions/soft-decoration/icons/services/service-culture-wall.png',
      '/images/solutions/soft-decoration/icons/services/service-brand-display.png',
      '/images/solutions/soft-decoration/icons/services/service-wall-decoration.png',
      '/images/solutions/soft-decoration/icons/services/service-custom-display.png',
      '/images/solutions/soft-decoration/icons/services/service-lighting-display.png',
      '/images/solutions/soft-decoration/icons/services/service-promotion-display.png',
    ],
    scenes: ['主题银行网点', '特色文化网点', '财富中心', '品牌展示区', '金融教育空间'],
    process: [
      ['设计意图确认', '确认主题、材料方向与现场限制。'],
      ['材料深化', '完成尺寸、节点、材质与工艺细化。'],
      ['样式确认', '对关键工艺和效果进行制作确认。'],
      ['工厂制作', '多材料组合加工与成品检查。'],
      ['现场安装收口', '协调安装、固定、边缘和细节收口。'],
    ],
    processIcons: [
      '/images/solutions/soft-decoration/icons/process/process-design-confirmation.png',
      '/images/solutions/soft-decoration/icons/process/process-material-detail.png',
      '/images/solutions/soft-decoration/icons/process/process-style-confirmation.png',
      '/images/solutions/soft-decoration/icons/process/process-factory-production.png',
      '/images/solutions/soft-decoration/icons/process/process-installation-finishing.png',
    ],
    strengths: ['设计意图落地', '材料深化', '多材料组合', '工艺确认', '现场安装', '细节收口'],
    strengthIcons: [
      '/images/solutions/soft-decoration/icons/advantages/advantage-design-realization.png',
      '/images/solutions/soft-decoration/icons/advantages/advantage-material-detail.png',
      '/images/solutions/soft-decoration/icons/advantages/advantage-material-combination.png',
      '/images/solutions/soft-decoration/icons/advantages/advantage-process-confirmation.png',
      '/images/solutions/soft-decoration/icons/advantages/advantage-site-installation.png',
      '/images/solutions/soft-decoration/icons/advantages/advantage-detail-finishing.png',
    ],
    relatedScopes: ['室内VI', '营销展示', '服务区'],
  },
  {
    slug: 'atm',
    heroPoints: ['设备结构适配', '钣金加工能力', '品牌形象统一', '现场安装可靠'],
    heroPointIcons: [
      '/images/solutions/atm/icons/hero/atm-01-device-structure.png',
      '/images/solutions/atm/icons/hero/atm-02-sheet-metal-capability.png',
      '/images/solutions/atm/icons/hero/atm-03-brand-unified.png',
      '/images/solutions/atm/icons/hero/atm-04-site-installation.png',
    ],
    overview: '围绕自助银行和24小时服务区，提供ATM防护舱、智能设备外壳、设备围挡、防护结构与自助区标识相关配套服务。',
    services: [
      ['ATM防护舱', '兼顾设备安全、使用体验与维护效率。'],
      ['自助银行形象配套', '统一自助服务区品牌界面。'],
      ['智能设备外壳', '适配设备尺寸与现场使用需求。'],
      ['钣金结构', '完成结构深化、加工与表面处理。'],
      ['设备围挡 / 防护', '提升设备区安全与空间秩序。'],
      ['24小时自助区标识', '明确服务区识别与导向信息。'],
    ],
    serviceIcons: [
      '/images/solutions/atm/icons/services/atm-05-protection-cabin.png',
      '/images/solutions/atm/icons/services/atm-06-self-service-branding.png',
      '/images/solutions/atm/icons/services/atm-07-device-shell.png',
      '/images/solutions/atm/icons/services/atm-08-sheet-metal-structure.png',
      '/images/solutions/atm/icons/services/atm-09-device-guard.png',
      '/images/solutions/atm/icons/services/atm-10-service-zone-24h.png',
    ],
    scenes: ['自助银行', '24小时服务区', '离行式ATM', '智能柜员设备区', '社区金融服务点'],
    process: [
      ['设备尺寸确认', '核对设备规格、维护方式与现场界面。'],
      ['结构深化', '完成钣金、固定与防护节点深化。'],
      ['金属加工制作', '开展加工、表面处理与组装。'],
      ['设备适配安装', '配合现场条件完成安装调试。'],
      ['安全检查验收', '检查固定、安全与品牌统一效果。'],
    ],
    processIcons: [
      '/images/solutions/atm/icons/process/atm-11-device-size-check.png',
      '/images/solutions/atm/icons/process/atm-12-structure-detail.png',
      '/images/solutions/atm/icons/process/atm-13-metal-production.png',
      '/images/solutions/atm/icons/process/atm-14-device-fit-install.png',
      '/images/solutions/atm/icons/process/atm-15-safety-acceptance.png',
    ],
    strengths: ['钣金结构深化', '金属加工', '表面处理', '设备适配', '品牌统一', '现场安装'],
    strengthIcons: [
      '/images/solutions/atm/icons/advantages/atm-16-sheet-metal-detail.png',
      '/images/solutions/atm/icons/advantages/atm-17-metal-processing.png',
      '/images/solutions/atm/icons/advantages/atm-18-surface-finish.png',
      '/images/solutions/atm/icons/advantages/atm-19-device-fit.png',
      '/images/solutions/atm/icons/advantages/atm-20-brand-unified.png',
      '/images/solutions/atm/icons/advantages/atm-21-site-installation.png',
    ],
    relatedScopes: ['ATM防护', '自助区', '24小时区'],
  },
  {
    slug: 'upgrade',
    heroPoints: ['多网点统筹', '标准化深化', '批量生产交付', '统一验收维护'],
    heroPointIcons: [
      '/images/solutions/upgrade/icons/hero/upgrade-01-network-management.png',
      '/images/solutions/upgrade/icons/hero/upgrade-02-standard-detail.png',
      '/images/solutions/upgrade/icons/hero/upgrade-03-batch-delivery.png',
      '/images/solutions/upgrade/icons/hero/upgrade-04-acceptance-maintenance.png',
    ],
    overview: '面向年度VI改造、多网点门楣升级、标识系统更新与批量设备更换，提供从标准深化、生产排期到多地区安装协调的一体化支持。',
    services: [
      ['多网点统筹', '统一项目节奏，协调多网点同步推进。'],
      ['标准化深化', '形成可复制、可批量执行的深化图纸。'],
      ['批量生产', '依托制造体系保障标准统一。'],
      ['分批交付', '按区域、批次和现场条件有序交付。'],
      ['多地区安装协调', '统筹进场、施工、安装和现场沟通。'],
      ['统一验收', '按照标准完成质量检查与交付确认。'],
    ],
    serviceIcons: [
      '/images/solutions/upgrade/icons/services/upgrade-05-network-coordination.png',
      '/images/solutions/upgrade/icons/services/upgrade-06-standard-design.png',
      '/images/solutions/upgrade/icons/services/upgrade-07-batch-production.png',
      '/images/solutions/upgrade/icons/services/upgrade-08-staged-delivery.png',
      '/images/solutions/upgrade/icons/services/upgrade-09-regional-installation.png',
      '/images/solutions/upgrade/icons/services/upgrade-10-unified-acceptance.png',
    ],
    scenes: ['年度VI改造', '多网点门楣升级', '标识系统更新', 'VI换版', '批量设备 / 标识更换', '分支机构统一建设'],
    process: [
      ['改造清单确认', '整理网点范围、品类与执行标准。'],
      ['标准深化', '统一图纸、材料、规格与工艺要求。'],
      ['批量排产', '按批次组织生产制造和品控。'],
      ['物流安装协调', '安排分批发运、进场和安装。'],
      ['统一验收服务', '配合验收、维护与售后支持。'],
    ],
    processIcons: [
      '/images/solutions/upgrade/icons/process/upgrade-11-renovation-checklist.png',
      '/images/solutions/upgrade/icons/process/upgrade-12-standard-detail.png',
      '/images/solutions/upgrade/icons/process/upgrade-13-production-scheduling.png',
      '/images/solutions/upgrade/icons/process/upgrade-14-logistics-installation.png',
      '/images/solutions/upgrade/icons/process/upgrade-15-acceptance-service.png',
    ],
    strengths: ['设计深化', '生产制造', '项目管理', '物流协调', '现场安装', '售后服务'],
    strengthIcons: [
      '/images/solutions/upgrade/icons/advantages/upgrade-16-design-detail.png',
      '/images/solutions/upgrade/icons/advantages/upgrade-17-manufacturing.png',
      '/images/solutions/upgrade/icons/advantages/upgrade-18-project-management.png',
      '/images/solutions/upgrade/icons/advantages/upgrade-19-logistics-coordination.png',
      '/images/solutions/upgrade/icons/advantages/upgrade-20-site-installation.png',
      '/images/solutions/upgrade/icons/advantages/upgrade-21-after-sales-service.png',
    ],
    relatedScopes: ['升级改造', '外立面', '标识', '维护'],
  },
]

const pageMeta: Record<string, { title: string; description: string }> = {
  '/': { title: '广东昊野智能技术有限公司｜金融空间VI标识整体解决方案', description: '广东昊野智能技术有限公司专注金融空间VI标识深化设计、智能制造、工程实施与长期服务。' },
  '/about': { title: '关于昊野｜广东昊野智能技术有限公司', description: '了解广东昊野智能技术有限公司、Since 2006、企业数据与金融空间VI标识服务能力。' },
  '/solutions': { title: '金融空间VI标识解决方案｜昊野智能', description: '昊野智能提供银行网点外立面形象、营业厅室内VI标识、特色网点软装落地、ATM防护和网点批量升级改造。' },
  '/projects': { title: '金融空间VI标识项目案例｜昊野智能', description: '查看昊野智能当前项目案例参考、银行网点类型、地区与服务范围。' },
  '/manufacturing': { title: '制造实力｜昊野智能', description: '了解昊野智能生产与装配空间、制造交付团队、工艺设备协同与工厂图片。' },
  '/service': { title: '项目交付与服务保障｜昊野智能', description: '了解昊野智能从需求调研到售后服务的七步项目流程与服务保障。' },
  '/qualifications': { title: '资质与荣誉｜昊野智能', description: '查看昊野智能质量、环境、职业健康安全管理体系及工程施工相关资质证书。' },
  '/contact': { title: '联系我们｜昊野智能', description: '联系广东昊野智能技术有限公司，提交金融空间VI标识项目咨询。' },
}

function resolveMeta(pathname: string) {
  if (pathname.startsWith('/solutions/') && pathname !== '/solutions') {
    const index = solutionRoutes.findIndex((item) => pathname === item.path)
    const solution = solutions[index]
    if (solution) {
      return { title: `${solution.title}｜昊野智能`, description: solution.description }
    }
  }
  if (pathname.startsWith('/projects/') && pathname !== '/projects') {
    const project = projects.find((item) => pathname === `/projects/${item.slug}`)
    if (project) {
      return { title: `${project.bank}${project.title}｜昊野智能`, description: `${project.category}项目案例参考，展示${project.scope}相关项目图片与服务能力。` }
    }
  }
  return pageMeta[pathname] ?? { title: '页面未找到｜昊野智能', description: '该页面暂不存在，可返回昊野智能官网首页继续浏览。' }
}

function Seo() {
  const { pathname } = useLocation()

  useEffect(() => {
    const meta = resolveMeta(pathname)
    document.title = meta.title
    let description = document.querySelector<HTMLMetaElement>('meta[name="description"]')
    if (!description) {
      description = document.createElement('meta')
      description.name = 'description'
      document.head.appendChild(description)
    }
    description.content = meta.description
  }, [pathname])

  return null
}

function ScrollToTop() {
  const { pathname } = useLocation()

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 })
  }, [pathname])

  return null
}

function useHomeMotion() {
  useEffect(() => {
    const root = document.querySelector('.home-motion-root')
    if (!root || !('IntersectionObserver' in window)) return

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const revealTargets = Array.from(root.querySelectorAll<HTMLElement>('[data-reveal]'))
    const statTargets = Array.from(root.querySelectorAll<HTMLElement>('.stat-card'))
    root.classList.add('motion-ready')

    const finishCount = (element: HTMLElement) => {
      const value = element.querySelector<HTMLElement>('.stat-value')
      if (!value) return
      value.textContent = `${value.dataset.countTarget ?? value.textContent ?? ''}${value.dataset.countSuffix ?? ''}`
    }

    if (prefersReducedMotion) {
      revealTargets.forEach((element) => element.classList.add('is-visible'))
      statTargets.forEach((element) => finishCount(element))
      return () => root.classList.remove('motion-ready')
    }

    const revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        entry.target.classList.add('is-visible')
        revealObserver.unobserve(entry.target)
      })
    }, { threshold: 0.16, rootMargin: '0px 0px -8% 0px' })

    const countObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        const card = entry.target as HTMLElement
        const value = card.querySelector<HTMLElement>('.stat-value')
        if (!value || value.dataset.counted === 'true') return

        value.dataset.counted = 'true'
        const target = Number(value.dataset.countTarget ?? '0')
        const suffix = value.dataset.countSuffix ?? ''
        if (value.dataset.countMode === 'static' || !Number.isFinite(target)) {
          finishCount(card)
          countObserver.unobserve(card)
          return
        }

        const start = 0
        const duration = 1000
        const startedAt = performance.now()

        const tick = (now: number) => {
          const progress = Math.min((now - startedAt) / duration, 1)
          const eased = 1 - Math.pow(1 - progress, 3)
          value.textContent = `${Math.round(start + (target - start) * eased)}${suffix}`
          if (progress < 1) requestAnimationFrame(tick)
        }

        requestAnimationFrame(tick)
        countObserver.unobserve(card)
      })
    }, { threshold: 0.45 })

    revealTargets.forEach((element) => revealObserver.observe(element))
    statTargets.forEach((element) => countObserver.observe(element))

    return () => {
      revealObserver.disconnect()
      countObserver.disconnect()
      root.classList.remove('motion-ready')
    }
  }, [])
}

function ImageFrame({ asset, mobileAsset, className = '', label = '临时视觉占位', loading = 'lazy' }: { asset: ImageAsset; mobileAsset?: ImageAsset; className?: string; label?: string; loading?: 'lazy' | 'eager' }) {
  const [failed, setFailed] = useState(false)
  const mobileSrc = mobileAsset?.src ?? asset.mobileSrc

  return (
    <div className={`image-frame ${className}`}>
      {asset.src && !failed ? (
        mobileSrc ? (
          <picture>
            <source media="(max-width: 767px)" srcSet={mobileSrc} />
            <img src={asset.src} alt={asset.alt} loading={loading} decoding="async" onError={() => setFailed(true)} />
          </picture>
        ) : (
          <img src={asset.src} alt={asset.alt} loading={loading} decoding="async" onError={() => setFailed(true)} />
        )
      ) : null}
      {!asset.src || failed ? <div className="image-placeholder" role="img" aria-label={label === '临时视觉占位' ? asset.alt : label} /> : null}
    </div>
  )
}

function BrandLogo({ location = 'header' }: { location?: 'header' | 'footer' }) {
  const [failed, setFailed] = useState(false)
  const source = location === 'footer' ? '/assets/haoye-logo-footer.png' : '/assets/haoye-logo.png.png'

  return (
    <span className={`brand-logo brand-logo-${location}`}>
      {!failed ? <img src={source} alt="昊野智能 Haoye Intelligent 官方 Logo" onError={() => setFailed(true)} /> : null}
    </span>
  )
}

function SectionHeading({ title, description, link, linkTo = '/contact' }: { title: string; description?: string; link?: string; linkTo?: string }) {
  return (
    <div className="section-heading section-header">
      <div>
        <h2 className="section-title">{title}</h2>
        {description ? <p className="section-subheading">{description}</p> : null}
      </div>
      {link ? <Link className="section-link section-header-link" to={linkTo}>{link} <span aria-hidden="true">→</span></Link> : null}
    </div>
  )
}

function Header() {
  const [scrolled, setScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)

  useEffect(() => {
    const sentinel = document.querySelector('.scroll-sentinel')
    if (!sentinel || !('IntersectionObserver' in window)) {
      setScrolled(window.scrollY > 12)
      return
    }

    const observer = new IntersectionObserver(([entry]) => {
      setScrolled(!entry.isIntersecting)
    })

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [])

  return (
    <header className={`master-header ${scrolled ? 'is-scrolled' : ''}`}>
      <div className="master-header-inner home-shell">
        <Link className="master-brand" to="/" aria-label="昊野智能 HAOYE 首页">
          <BrandLogo />
        </Link>
        <nav className={`master-nav ${menuOpen ? 'is-open' : ''}`} aria-label="主导航">
          {mainNavItems.map((item) => (
            <NavLink key={item.href} end={item.href === '/'} className={({ isActive }) => isActive ? 'is-active' : ''} to={item.href} onClick={() => setMenuOpen(false)}>{item.label}</NavLink>
          ))}
        </nav>
        <Link className="master-cta" to="/contact"><span>获取项目资料</span><span aria-hidden="true">→</span></Link>
        <button className={`master-menu-toggle ${menuOpen ? 'is-open' : ''}`} type="button" aria-label={menuOpen ? '关闭导航菜单' : '打开导航菜单'} aria-expanded={menuOpen} onClick={() => setMenuOpen((open) => !open)}>
          <span />
          <span />
          <span />
        </button>
      </div>
    </header>
  )
}

function Hero() {
  return (
    <section className="hero" id="home">
      <div className="hero-media" aria-hidden="true">
        <ImageFrame asset={heroImage} label="银行网点外立面图片占位" loading="eager" />
      </div>
      <div className="hero-scrim" />
      <div className="hero-content home-shell">
        <div className="hero-copy">
          <h1 className="hero-title">
            让金融VI标准
            <br className="hero-break-mobile" />
            <span>精准落地</span>
          </h1>
          <p className="hero-lead hero-subtitle">金融空间VI标识整体解决方案服务商</p>
          <p className="hero-description hero-copy-text">
            <span className="hero-description-desktop">专注金融机构营业网点VI标识的设计深化、智能制造、工程实施与长期服务。</span>
            <span className="hero-description-mobile">专注金融机构营业网点VI标识的设计深化、<br />智能制造、工程实施与长期服务</span>
          </p>
          <div className="hero-actions">
            <Link className="button button-primary" to="/solutions">了解解决方案 <span aria-hidden="true">→</span></Link>
            <Link className="button button-quiet" to="/projects">查看项目案例 <span aria-hidden="true">→</span></Link>
          </div>
          <div className="hero-meta hero-since"><span className="meta-rule" /> <span>Since 2006</span></div>
        </div>
      </div>
    </section>
  )
}

function ClientMarquee() {
  const availableBanks = bankClients.filter((bank) => bank.logo && bank.logo.complete !== false)
  const marqueeItems = [...availableBanks, ...availableBanks]
  return (
    <section className="client-strip" aria-label="服务客户">
      <div className="home-shell client-strip-heading">
        <div className="client-strip-label"><span>服务客户</span><i aria-hidden="true" /></div>
        <Link className="client-more" to="/contact">更多客户 <span aria-hidden="true">→</span></Link>
      </div>
      <div className="marquee-window">
        <div className="marquee-track">
          {marqueeItems.map((bank, index) => (
            <div className={`bank-wordmark tone-${bank.tone}`} data-bank={bank.name} key={`${bank.name}-${index}`} aria-label={bank.name}>
              <img className={`bank-logo-image ${bank.logo!.fit === 'padded' ? 'is-padded' : ''}`} src={bank.logo!.src} alt={`${bank.name}官方品牌Logo`} loading="eager" />
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function StatIcon({ src, label }: { src: string; label: string }) {
  return (
    <span className="stat-icon" aria-hidden="true">
      <img src={src} alt={`${label}图标`} loading="lazy" decoding="async" />
    </span>
  )
}

function CompanyIntro() {
  return (
    <section className="company-section section-light" id="about" data-reveal>
      <div className="site-container company-grid">
        <div className="company-copy">
          <h2 className="company-title">广东昊野智能技术有限公司</h2>
          <p>专注金融空间VI标识系统的设计深化、智能制造与工程实施，为银行网点及金融机构提供标准化、模块化、可落地的品牌形象整体解决方案。</p>
          <p>自2006年成立以来，昊野持续服务银行及金融机构，助力品牌形象统一落地与网点体验升级。</p>
          <Link className="text-link" to="/about">了解昊野 <span aria-hidden="true">→</span></Link>
        </div>
        <div className="stats-grid">
          {companyStats.map((stat) => (
            <div className="stat-card" key={stat.label}>
              <StatIcon src={stat.icon} label={stat.label} />
              <strong className="stat-number">
                <span className="stat-value" data-count-mode="count" data-count-target={stat.value.replace(/\D/g, '')} data-count-suffix={stat.value.includes('+') ? '+' : ''}>{stat.value}</span>
                <small className="stat-unit">{stat.unit}</small>
              </strong>
              <span className="stat-label">{stat.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function Solutions() {
  return (
    <section className="section section-solutions" id="solutions" data-reveal>
      <div className="site-container">
        <SectionHeading title="我们的解决方案" link="查看全部解决方案" linkTo="/solutions" />
        <div className="solution-grid">
          {solutions.map((solution, index) => (
            <Link className="solution-card" data-solution={index + 1} key={solution.title} to={solutionRoutes[index].path}>
              <ImageFrame asset={solution.image} mobileAsset={solution.mobileImage} className="solution-image" />
              <div className="solution-body">
                <h3>{solution.title}</h3>
                <p>{solution.description}</p>
                <span className="card-arrow" aria-label={`了解${solution.title}`}>详情 <span aria-hidden="true">→</span></span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  )
}

function AdvantageIcon({ src, alt }: { src: string; alt: string }) {
  return <img className="advantage-graphic" src={src} alt={alt} loading="lazy" />
}

function Advantages() {
  return (
    <section className="section section-advantages" data-reveal>
      <div className="site-container">
        <SectionHeading title="为什么选择昊野" />
        <div className="advantage-grid">
          {advantages.map((item) => (
            <article className="advantage-card" key={item.title}>
              <span className="advantage-icon"><AdvantageIcon src={item.icon} alt={`${item.title}图标`} /></span>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}

function Projects() {
  const categories = ['全部', '国有银行', '股份制银行', '城商行', '农商行', '其他']
  const [activeCategory, setActiveCategory] = useState('全部')
  const [page, setPage] = useState(0)
  const filteredProjects = useMemo(() => activeCategory === '全部' ? projects : projects.filter((project) => project.category === activeCategory), [activeCategory])
  const pageSize = 4
  const pageCount = Math.max(1, Math.ceil(filteredProjects.length / pageSize))
  const visibleProjects = filteredProjects.slice(page * pageSize, page * pageSize + pageSize)

  useEffect(() => setPage(0), [activeCategory])

  return (
    <section className="section section-projects" id="projects" data-reveal>
      <div className="site-container">
        <SectionHeading title="精选项目案例" link="查看全部案例" linkTo="/projects" />
        <div className="project-filters" role="tablist" aria-label="项目案例分类">
          {categories.map((category) => (
            <button key={category} type="button" className={activeCategory === category ? 'is-active' : ''} onClick={() => setActiveCategory(category)} role="tab" aria-selected={activeCategory === category}>{category}</button>
          ))}
        </div>
        <div className="project-grid-wrap">
          <div className="project-grid">
            {visibleProjects.map((project, index) => (
            <Link className={`project-card ${index === 0 ? 'project-card--featured' : index === 3 ? 'project-card--wide' : 'project-card--compact'}`} data-project-card={index + 1} key={`${project.bank}-${project.title}`} to={`/projects/${project.slug}`}>
              <ImageFrame asset={project.image} className="project-image" />
              <div className="project-body">
                <div className="project-bank">{project.bank}</div>
                <h3>{project.title}</h3>
                <p>{project.location}</p>
                <span>{project.scope}</span>
              </div>
            </Link>
            ))}
          </div>
          {pageCount > 1 ? (
            <div className="project-pagination" aria-label="项目案例翻页">
              <button type="button" aria-label="上一组案例" disabled={page === 0} onClick={() => setPage((current) => Math.max(0, current - 1))}>←</button>
              <span>{String(page + 1).padStart(2, '0')} / {String(pageCount).padStart(2, '0')}</span>
              <button type="button" aria-label="下一组案例" disabled={page === pageCount - 1} onClick={() => setPage((current) => Math.min(pageCount - 1, current + 1))}>→</button>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  )
}

function ProjectProcess() {
  return (
    <section className="section section-process section-light" id="process" data-reveal>
      <div className="site-container">
          <SectionHeading title="从设计深化到安装交付" description="以标准化项目流程衔接设计、制造、施工与长期服务" link="了解服务体系" linkTo="/service" />
        <div className="process-track">
          {processSteps.map((step) => (
            <div className="process-step" key={step.id}>
              <div className="process-icon-wrap">
                <img className="process-icon" src={step.icon} alt={`${step.title}图标`} loading="lazy" />
              </div>
              <div className="process-copy">
                <span className="process-number">{step.id}</span>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function Manufacturing() {
  return (
    <section className="section section-manufacturing" id="manufacturing" data-reveal>
      <div className="site-container manufacturing-grid">
        <div className="manufacturing-copy">
          <SectionHeading title="制造实力" description="自有生产基地，完善的设备与工艺体系，通过标准化生产与品质管控保障项目稳定交付" />
          <div className="manufacturing-stats">
            {manufacturingStats.map((stat) => (
              <div className="manufacturing-stat" key={stat.label}><strong>{stat.value}<small>{stat.unit}</small></strong><span>{stat.label}</span></div>
            ))}
          </div>
          <div className="manufacturing-actions"><Link className="button button-primary" to="/manufacturing">了解制造能力 <span aria-hidden="true">→</span></Link><Link className="text-link" to="/contact">预约项目沟通</Link></div>
        </div>
        <div className="manufacturing-gallery">
          {manufacturingImages.map((image, index) => <ImageFrame key={image.sourceId} asset={image} className={`manufacturing-image image-${index + 1}`} />)}
        </div>
      </div>
    </section>
  )
}

function CredentialsInsights() {
  return (
    <section className="credentials-insights section-light" data-reveal>
      <div className="site-container credentials-insights-grid">
        <div className="credentials-column">
          <SectionHeading title="资质与荣誉" link="查看资质资料" linkTo="/qualifications" />
          <div className="qualification-grid">
            {qualifications.slice(0, 4).map((item) => (
              <div className="qualification-card" key={item.title}>
                <div className="qualification-placeholder">
                  <img className="qualification-image" src={item.image.src} alt={item.image.alt} loading="lazy" decoding="async" />
                </div>
                <p>{item.title}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="service-column">
          <SectionHeading title="服务保障" link="了解服务体系" linkTo="/service" />
          <div className="service-guarantee-grid">
            {serviceGuarantees.map((item) => (
              <div className="service-guarantee-item" key={item.title}>
                <div className="service-guarantee-top">
                  <span className="service-guarantee-number">{item.id}</span>
                  <img className="service-guarantee-icon" src={item.icon} alt={`${item.title}图标`} loading="lazy" />
                </div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

function CTA() {
  return (
    <section className="cta-section" id="contact" data-reveal>
      <div className="site-container cta-inner">
        <div className="cta-copy"><h2>让品牌在每一个金融空间被看见</h2><p>为您的项目提供专业建议与定制方案</p></div>
        <Link className="button button-light" to="/contact">获取项目资料 <span aria-hidden="true">→</span></Link>
      </div>
    </section>
  )
}

function Footer() {
  const footerNavItems = [
    mainNavItems[0],
    mainNavItems[1],
    mainNavItems[2],
    mainNavItems[3],
    mainNavItems[7],
  ]

  return (
    <footer className="site-footer">
      <div className="site-container footer-main">
        <div className="footer-brand"><Link className="footer-brand-logo" to="/" aria-label="昊野智能 Haoye Intelligent 官方 Logo"><BrandLogo location="footer" /></Link><Link className="footer-brand-name" to="/">广东昊野智能技术有限公司</Link><p>专注金融空间VI标识深化设计、智能制造、工程实施与长期服务</p><small>金融空间VI标识整体解决方案服务商</small></div>
        <div className="footer-contact">
          <strong>联系方式</strong>
          <a className="footer-contact-row" href={`tel:${contactPhone}`}><span className="footer-contact-label">电话：</span><span className="footer-contact-value">{contactPhone}</span></a>
          <a className="footer-contact-row" href={`mailto:${contactEmail}`}><span className="footer-contact-label">邮箱：</span><span className="footer-contact-value">{contactEmail}</span></a>
          <a className="footer-contact-row is-address" href={companyMapUrl} target="_blank" rel="noreferrer"><span className="footer-contact-label">地址：</span><span className="footer-contact-value">{companyAddress}</span></a>
        </div>
        <div className="footer-links"><strong>快速导航</strong>{footerNavItems.map((item) => <Link to={item.href} key={item.href}>{item.label}</Link>)}</div>
        <div className="footer-qr"><strong>微信咨询</strong><div className="qr-placeholder"><img src={wechatQrCode} alt="微信咨询二维码" loading="lazy" decoding="async" /></div><small>扫码添加微信咨询</small></div>
      </div>
      <div className="site-container footer-bottom">
        <span>© 2006-2026 广东昊野智能技术有限公司</span>
        <div className="footer-bottom-links"><a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">粤ICP备2026136084号-1</a><a className="footer-public-security" href="https://beian.mps.gov.cn/" target="_blank" rel="noopener noreferrer"><img src="/assets/icons/public-security-filing.png" alt="公安联网备案图标" />粤公网安备 44060602003596号</a><Link to="/contact">隐私政策</Link><Link to="/">网站地图</Link></div>
      </div>
    </footer>
  )
}

export function HomePage() {
  useHomeMotion()

  return (
    <div className="home-motion-root">
      <Hero />
      <ClientMarquee />
      <CompanyIntro />
      <div className="page-two">
        <Solutions />
        <Advantages />
      </div>
      <Projects />
      <ProjectProcess />
      <Manufacturing />
      <CredentialsInsights />
      <CTA />
    </div>
  )
}

function PageHero({ title, description, current }: { title: string; description: string; current: string }) {
  return (
    <section className="page-hero">
      <div className="site-container">
        <nav className="breadcrumb" aria-label="面包屑"><Link to="/">首页</Link><span>/</span><span>{current}</span></nav>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
    </section>
  )
}

function PageContainer({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <section className={`section subpage-section ${className}`}>
      <div className="site-container">{children}</div>
    </section>
  )
}

function AboutPage() {
  return (
    <>
      <PageHero title="关于昊野" current="关于昊野" description="金融空间 VI 标识整体解决方案服务商" />
      <CompanyIntro />
      <Advantages />
      <PageContainer className="subpage-entry-section">
        <div className="subpage-entry-grid">
          <Link className="detail-panel subpage-entry-card" to="/manufacturing">
            <h3>制造能力</h3>
            <p>查看生产与装配空间、工艺设备协同及当前工厂图片资料。</p>
            <span>了解制造实力 →</span>
          </Link>
          <Link className="detail-panel subpage-entry-card" to="/service">
            <h3>项目服务</h3>
            <p>了解从需求调研到售后服务的七步项目交付流程。</p>
            <span>了解服务体系 →</span>
          </Link>
        </div>
      </PageContainer>
      <CTA />
    </>
  )
}

function SolutionsPage() {
  return (
    <>
      <PageHero title="解决方案" current="解决方案" description="围绕银行网点外立面、室内VI、软装落地、ATM防护与批量升级改造提供整体服务。" />
      <PageContainer>
        <div className="solutions-overview-list">
          {solutions.map((solution, index) => (
            <Link className="solution-overview-item" data-solution={index + 1} key={solution.title} to={solutionRoutes[index].path}>
              <ImageFrame asset={solution.image} className="solution-overview-image" />
              <div className="solution-overview-copy">
                <span>{String(index + 1).padStart(2, '0')}</span>
                <h2>{solution.title}</h2>
                <p>{solution.description}</p>
                <strong>查看详情 →</strong>
              </div>
            </Link>
          ))}
        </div>
      </PageContainer>
    </>
  )
}

function SolutionDetailPage() {
  const { slug } = useParams()
  const index = solutionRoutes.findIndex((item) => item.slug === slug)
  const solution = solutions[index]
  const detail = solutionDetailPages[index]

  if (!solution || !detail) {
    return <Navigate to="/solutions" replace />
  }

  const sceneImages = projects.slice(index, index + 5).length >= 5 ? projects.slice(index, index + 5) : projects.slice(0, 5)
  const relatedProjects = projects.filter((project) => detail.relatedScopes.some((scope) => project.scope.includes(scope))).slice(0, 3)
  const capabilityImage = manufacturingImages[index % manufacturingImages.length] ?? solution.image

  return (
    <>
      <section className="solution-hero">
        <ImageFrame asset={solution.image} className="solution-hero-media" loading="eager" />
        <div className="solution-hero-scrim" />
        <div className="site-container solution-hero-content">
          <nav className="solution-breadcrumb" aria-label="面包屑"><Link to="/">首页</Link><span>/</span><Link to="/solutions">解决方案</Link><span>/</span><span>{solution.title}</span></nav>
          <h1>{solution.title}</h1>
          <p>{solution.description}</p>
          <div className="solution-hero-points">
            {detail.heroPoints.map((item, heroPointIndex) => {
              const icon = detail.heroPointIcons?.[heroPointIndex]

              return (
                <span key={item}>
                  <i className={icon ? 'has-icon' : undefined} aria-hidden="true">
                    {icon ? <img src={icon} alt="" loading="eager" decoding="async" /> : null}
                  </i>
                  {item}
                </span>
              )
            })}
          </div>
        </div>
      </section>

      <section className="solution-section solution-overview-panel">
        <div className="site-container solution-centered">
          <h2>解决方案概述</h2>
          <p>{detail.overview}</p>
        </div>
      </section>

      <section className="solution-section solution-services-panel">
        <div className="site-container">
          <h2 className="solution-panel-title">核心服务内容</h2>
          <div className="solution-service-grid">
            {detail.services.map(([title, description], serviceIndex) => {
              const icon = detail.serviceIcons?.[serviceIndex]

              return (
                <article className="solution-service-card" key={title}>
                  <span className={`solution-line-icon${icon ? ' has-icon' : ''}`} aria-hidden="true">
                    {icon ? <img src={icon} alt="" loading="lazy" decoding="async" /> : String(serviceIndex + 1).padStart(2, '0')}
                  </span>
                  <h3>{title}</h3>
                  <p>{description}</p>
                </article>
              )
            })}
          </div>
        </div>
      </section>

      <section className="solution-section solution-scenes-panel">
        <div className="site-container">
          <h2 className="solution-panel-title">典型应用场景</h2>
          <div className="solution-scene-grid">
            {detail.scenes.map((scene, sceneIndex) => (
              <article className="solution-scene-card" key={scene}>
                <ImageFrame asset={sceneImages[sceneIndex % sceneImages.length].image} className="solution-scene-image" />
                <h3>{scene}</h3>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="solution-section solution-process-panel">
        <div className="site-container">
          <h2 className="solution-panel-title">实施流程</h2>
          <div className="solution-process-track">
            {detail.process.map(([title, description], processIndex) => {
              const icon = detail.processIcons?.[processIndex]

              return (
                <article className="solution-process-step" key={title}>
                  <span className={`solution-process-icon${icon ? ' has-icon' : ''}`} aria-hidden="true">
                    {icon ? <img src={icon} alt="" loading="lazy" decoding="async" /> : String(processIndex + 1).padStart(2, '0')}
                  </span>
                  {icon ? <span className="solution-process-number">{String(processIndex + 1).padStart(2, '0')}</span> : null}
                  <h3>{title}</h3>
                  <p>{description}</p>
                </article>
              )
            })}
          </div>
        </div>
      </section>

      <section className="solution-section solution-strength-panel">
        <div className="site-container solution-strength-layout">
          <ImageFrame asset={capabilityImage} className="solution-strength-image" />
          <div>
            <h2 className="solution-panel-title">能力与优势</h2>
            <div className="solution-strength-grid">
              {detail.strengths.map((item, strengthIndex) => {
                const icon = detail.strengthIcons?.[strengthIndex]

                return (
                  <article className="solution-strength-item" key={item}>
                    <span className={`solution-line-icon${icon ? ' has-icon' : ''}`} aria-hidden="true">
                      {icon ? <img src={icon} alt="" loading="lazy" decoding="async" /> : null}
                    </span>
                    <h3>{item}</h3>
                    <p>围绕项目深化、制造与现场交付，保障方案稳定落地。</p>
                  </article>
                )
              })}
            </div>
          </div>
        </div>
      </section>

      {relatedProjects.length ? (
        <section className="solution-section solution-related-panel">
          <div className="site-container">
            <h2 className="solution-panel-title">相关项目案例</h2>
            <div className="solution-related-grid">
              {relatedProjects.map((project) => (
                <Link className="project-card solution-related-card" to={`/projects/${project.slug}`} key={project.slug}>
                  <ImageFrame asset={project.image} className="project-image" />
                  <div className="project-body">
                    <div className="project-bank">{project.bank}</div>
                    <h3>{project.title}</h3>
                    <p>{project.location}</p>
                    <span>{project.scope}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <section className="solution-detail-cta">
        <div className="site-container solution-detail-cta-inner">
          <div>
            <h2>需要定制{solution.title}解决方案？</h2>
            <p>昊野团队可提供设计深化、生产制造、工程实施与长期服务支持。</p>
          </div>
          <Link className="button button-light" to="/contact">立即咨询 <span aria-hidden="true">→</span></Link>
        </div>
      </section>
    </>
  )
}

function ProjectsPage() {
  return (
    <>
      <PageHero title="项目案例" current="项目案例" description="展示当前已确认的项目图片、项目标题、地区与服务范围。" />
      <Projects />
    </>
  )
}

function ProjectDetailHero({ project }: { project: Project }) {
  return (
    <section className="project-detail-hero">
      {project.image.src ? <img src={project.image.src} alt={project.image.alt} loading="eager" decoding="async" /> : null}
      <div className="project-detail-hero-scrim" />
      <div className="site-container project-detail-hero-content">
        <Link className="project-back-link" to="/projects">← 返回项目案例</Link>
        <span className="project-detail-category">{project.category}</span>
        <h1>{project.bank}{project.title}</h1>
        <div className="project-detail-meta">
          <span>{project.bank}</span>
          {project.location ? <span>{project.location}</span> : null}
          <span>{project.scope}</span>
        </div>
      </div>
    </section>
  )
}

function ProjectInfo({ project }: { project: Project }) {
  const fields = [
    { label: '客户名称', value: project.bank },
    { label: '项目类型', value: project.category },
    { label: '项目地区', value: project.location },
    { label: '服务内容', value: project.scope },
    { label: '项目时间', value: project.year },
  ].filter((item) => item.value)

  return (
    <div className="project-info-grid">
      {fields.map((item) => (
        <div className="project-info-item" key={item.label}>
          <span>{item.label}</span>
          <strong>{item.value}</strong>
        </div>
      ))}
    </div>
  )
}

function ProjectMiniCard({ project }: { project: Project }) {
  return (
    <Link className="related-project-card" to={`/projects/${project.slug}`}>
      <ImageFrame asset={project.image} className="related-project-image" />
      <div>
        <span>{project.category}</span>
        <h3>{project.bank}{project.title}</h3>
      </div>
    </Link>
  )
}

function ProjectDetailPage() {
  const { slug } = useParams()
  const projectIndex = projects.findIndex((item) => item.slug === slug)
  const project = projects[projectIndex]

  if (!project) {
    return <Navigate to="/projects" replace />
  }

  const previousProject = projectIndex > 0 ? projects[projectIndex - 1] : undefined
  const nextProject = projectIndex < projects.length - 1 ? projects[projectIndex + 1] : undefined
  const gallery = project.gallery?.length ? project.gallery : [project.image]
  const scopeItems = project.scope.split('/').map((item) => item.trim()).filter(Boolean)
  const relatedProjects = projects.filter((item) => item.slug !== project.slug && item.category === project.category).slice(0, 3)

  return (
    <>
      <ProjectDetailHero project={project} />
      <PageContainer className="project-detail-section">
        <ProjectInfo project={project} />

        <div className="project-detail-layout">
          <div className="project-overview">
            <SectionHeading title="项目概览" description={project.overview ?? '围绕金融空间 VI 标识相关深化、生产及项目落地服务，呈现当前已确认的项目图片、类型与服务范围信息。'} />
          </div>
          <div className="project-scope-list">
            <h2 className="project-subtitle">项目实施内容</h2>
            <div className="project-scope-grid">
              {scopeItems.map((item, index) => (
                <div className="project-scope-item" key={item}>
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <h3>{item}</h3>
                  <p>结合项目现场与金融空间 VI 标识需求，衔接深化、制作与落地执行。</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="project-gallery-section">
          <SectionHeading title="项目图片" />
          <div className="project-detail-gallery">
            {gallery.map((image, index) => (
              <ImageFrame asset={image} className={index === 0 ? 'project-gallery-image project-gallery-image--main' : 'project-gallery-image'} key={`${image.sourceId}-${index}`} />
            ))}
          </div>
        </div>

        <div className="project-capabilities detail-panel">
          <h2 className="project-subtitle">项目服务能力</h2>
          <p>昊野具备以下项目服务能力：</p>
          <div className="project-capability-list">
            {(project.serviceCapabilities ?? projectServiceCapabilities).map((item) => <span key={item}>{item}</span>)}
          </div>
        </div>

        <div className="project-neighbor-nav">
          {previousProject ? <Link to={`/projects/${previousProject.slug}`}>← 上一个项目<span>{previousProject.bank}{previousProject.title}</span></Link> : <span />}
          {nextProject ? <Link to={`/projects/${nextProject.slug}`}>下一个项目 →<span>{nextProject.bank}{nextProject.title}</span></Link> : <span />}
        </div>

        {relatedProjects.length ? (
          <div className="related-projects">
            <SectionHeading title="相关项目" />
            <div className="related-project-grid">
              {relatedProjects.map((item) => <ProjectMiniCard project={item} key={item.slug} />)}
            </div>
          </div>
        ) : null}
      </PageContainer>
      <section className="cta-section">
        <div className="site-container cta-inner">
          <div className="cta-copy"><h2>需要类似项目支持？</h2><p>为金融空间 VI 标识项目提供深化、制造、实施与交付支持。</p></div>
          <Link className="button button-light" to="/contact">项目咨询 <span aria-hidden="true">→</span></Link>
        </div>
      </section>
    </>
  )
}

function ManufacturingPage() {
  return (
    <>
      <PageHero title="制造实力" current="制造实力" description="自有生产基地与制造交付体系，支撑金融空间 VI 标识项目稳定落地。" />
      <Manufacturing />
      <PageContainer className="subpage-entry-section">
        <div className="detail-panel-grid detail-panel-grid--three">
          <div className="detail-panel"><h3>生产制造</h3><p>围绕金融空间 VI 标识相关产品进行标准化生产与批量交付协同。</p></div>
          <div className="detail-panel"><h3>品质控制</h3><p>配合项目节点进行材料、工艺、成品与安装交付质量检查。</p></div>
          <div className="detail-panel"><h3>工程协同</h3><p>衔接深化设计、生产排期、现场施工与安装验收。</p></div>
        </div>
      </PageContainer>
    </>
  )
}

function ServicePage() {
  return (
    <>
      <PageHero title="项目交付与服务保障" current="项目交付与服务保障" description="整合从需求调研到售后服务的七步流程，以及当前已确认的服务保障内容。" />
      <ProjectProcess />
      <section className="credentials-insights section-light">
        <div className="site-container">
          <SectionHeading title="服务保障" />
          <div className="service-guarantee-grid service-page-grid">
            {serviceGuarantees.map((item) => (
              <div className="service-guarantee-item" key={item.title}>
                <div className="service-guarantee-top">
                  <span className="service-guarantee-number">{item.id}</span>
                  <img className="service-guarantee-icon" src={item.icon} alt={`${item.title}图标`} loading="lazy" />
                </div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <PageContainer className="subpage-entry-section">
        <div className="detail-panel">
          <h3>项目协同与售后</h3>
          <p>围绕批量网点标准化建设及升级改造，衔接设计、制造、工程实施、安装验收与长期售后服务。</p>
        </div>
      </PageContainer>
    </>
  )
}

function QualificationsPage() {
  return (
    <>
      <PageHero title="资质与荣誉" current="资质与荣誉" description="展示昊野智能质量、环境、职业健康安全管理体系及工程施工相关资质证书。" />
      <section className="credentials-insights section-light">
        <div className="site-container">
          <SectionHeading title="资质与荣誉" />
          <div className="qualification-grid qualification-page-grid">
            {qualifications.map((item) => (
              <div className="qualification-card" key={item.title}>
                <div className="qualification-placeholder">
                  <img className="qualification-image" src={item.image.src} alt={item.image.alt} loading="lazy" decoding="async" />
                </div>
                <p>{item.title}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <PageContainer className="subpage-entry-section">
        <div className="detail-panel">
          <h3>证书资料</h3>
          <p>展示昊野智能当前管理体系与工程施工相关资质证书。</p>
        </div>
      </PageContainer>
    </>
  )
}

function ContactForm() {
  const [message, setMessage] = useState('')

  return (
    <form className="contact-form detail-panel" onSubmit={(event) => { event.preventDefault(); setMessage(`在线提交功能正在接入，如需项目咨询，请直接致电${contactPhone}或发送邮件至${contactEmail}`) }}>
      <h3>项目需求</h3>
      <label>姓名<input name="name" type="text" autoComplete="name" /></label>
      <label>公司<input name="company" type="text" autoComplete="organization" /></label>
      <label>联系电话<input name="phone" type="tel" autoComplete="tel" /></label>
      <label>项目类型<select name="projectType" defaultValue=""><option value="" disabled>请选择</option><option>银行网点外立面形象</option><option>营业厅室内 VI 标识</option><option>特色网点软装落地</option><option>智能设备与 ATM 防护</option><option>网点批量升级改造</option></select></label>
      <label>项目需求<textarea name="request" rows={4} /></label>
      <button className="button button-primary" type="submit">提交需求 <span aria-hidden="true">→</span></button>
      {message ? <p className="form-message" role="status">{message}</p> : null}
    </form>
  )
}

function ContactPage() {
  return (
    <>
      <PageHero title="联系我们" current="联系我们" description="如需金融空间VI标识、网点改造及相关项目咨询，欢迎联系我们" />
      <section className="section subpage-section">
        <div className="site-container contact-layout">
          <div className="contact-grid">
            <div className="detail-panel">
              <h3>联系方式</h3>
              <p>电话：<a href={`tel:${contactPhone}`}>{contactPhone}</a></p>
              <p>邮箱：<a href={`mailto:${contactEmail}`}>{contactEmail}</a></p>
              <p>地址：<a href={companyMapUrl} target="_blank" rel="noreferrer">{companyAddress}</a></p>
            </div>
            <div className="detail-panel">
              <h3>项目咨询</h3>
              <p>如需金融空间VI标识、导视、门楣招牌、软装及配套产品项目沟通，欢迎提交需求或直接联系我们</p>
            </div>
            <div className="detail-panel">
              <h3>公司位置</h3>
              <p>{companyAddress}</p>
              <p><a href={companyMapUrl} target="_blank" rel="noreferrer">打开高德地图导航</a></p>
              <div className="contact-qr-block">
                <strong>微信咨询</strong>
                <div className="qr-placeholder"><img src={wechatQrCode} alt="微信咨询二维码" loading="lazy" decoding="async" /></div>
              </div>
            </div>
          </div>
          <ContactForm />
        </div>
      </section>
    </>
  )
}

function NotFoundPage() {
  return (
    <>
      <PageHero title="页面未找到" current="404" description="该页面暂不存在，可返回首页或进入解决方案页面继续浏览。" />
      <PageContainer>
        <div className="not-found-panel detail-panel">
          <h3>继续浏览昊野官网</h3>
          <p>您可以返回首页，或查看金融空间 VI 标识解决方案与项目服务内容。</p>
          <div className="not-found-actions">
            <Link className="button button-primary" to="/">返回首页 <span aria-hidden="true">→</span></Link>
            <Link className="text-link" to="/solutions">查看解决方案 <span aria-hidden="true">→</span></Link>
          </div>
        </div>
      </PageContainer>
    </>
  )
}

function SiteLayout({ children }: { children: ReactNode }) {
  return (
    <div className="site-app">
      <span className="scroll-sentinel" aria-hidden="true" />
      <Header />
      <main>{children}</main>
      <Footer />
    </div>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <Seo />
      <ScrollToTop />
      <SiteLayout>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/solutions" element={<SolutionsPage />} />
          <Route path="/solutions/:slug" element={<SolutionDetailPage />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/projects/:slug" element={<ProjectDetailPage />} />
          <Route path="/manufacturing" element={<ManufacturingPage />} />
          <Route path="/service" element={<ServicePage />} />
          <Route path="/qualifications" element={<QualificationsPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </SiteLayout>
    </BrowserRouter>
  )
}
